import { ExpensePreviewDto, PreviewShareDto } from 'src/Services/API/generated';

/**
 * The parties a money transfer can be made of. The MoneyTransfer form encodes them as
 * `<kind>-<id>` in its selects, since a single Select has to carry three id namespaces.
 */
export type TransferPartyKind = 'user' | 'placeholder' | 'bank';

export interface TransferParty {
  kind: TransferPartyKind;
  id: string;
}

const PARTY_KINDS: TransferPartyKind[] = ['user', 'placeholder', 'bank'];

/** Amounts are floats server-side, so equality is only ever checked to the cent. */
export const REIMBURSEMENT_AMOUNT_TOLERANCE = 0.01;

export const parseTransferParty = (value: string | null | undefined): TransferParty | null => {
  if (!value) return null;
  // The prefixes carry no dash, so the first one separates kind from id (a UUID has dashes).
  const separatorIndex = value.indexOf('-');
  if (separatorIndex <= 0) return null;
  const kind = value.slice(0, separatorIndex) as TransferPartyKind;
  const id = value.slice(separatorIndex + 1);
  if (!PARTY_KINDS.includes(kind) || !id) return null;
  return { kind, id };
};

const getSharePartyId = (share: PreviewShareDto): string | undefined =>
  share.user?.id ?? share.placeholderMember?.id;

/**
 * Who advanced the money. A Contact payer is deliberately left out: the API refuses to
 * reimburse one, so such an expense must never show up in the picker.
 */
export const getExpensePayerParty = (expense: ExpensePreviewDto): TransferParty | null => {
  if (expense.paidBy) return { kind: 'user', id: expense.paidBy.id };
  if (expense.paidByPlaceholderMember) {
    return { kind: 'placeholder', id: expense.paidByPlaceholderMember.id };
  }
  if (expense.paidByBankAccount) return { kind: 'bank', id: expense.paidByBankAccount.id };
  return null;
};

/**
 * An expense the beneficiary of the transfer can be paid back for.
 *
 * `isReimbursed` is undefined on incomes and on settlements and only ever `false` on an
 * open expense, which is exactly the set the API accepts as a link — testing it also keeps
 * incomes out without needing a flag the preview does not expose.
 */
export const isReimbursableBy = (
  expense: ExpensePreviewDto,
  beneficiary: TransferParty
): boolean => {
  if (expense.isReimbursed !== false) return false;
  const payer = getExpensePayerParty(expense);
  return payer?.kind === beneficiary.kind && payer?.id === beneficiary.id;
};

/**
 * Case B: the sender carries a share of the expense and only settles their own part.
 * A missing sender reads as case A, which is what the amounts show before « De » is picked.
 */
export const isSenderParticipant = (
  expense: ExpensePreviewDto,
  sender: TransferParty | null
): boolean =>
  !!sender &&
  sender.kind !== 'bank' &&
  (expense.shares ?? []).some(share => getSharePartyId(share) === sender.id);

/**
 * What the API expects the transfer to be worth for this expense: the whole expense when
 * the sender is an outsider paying the payer back (case A), their own shares when they are
 * a participant settling up (case B).
 */
export const getExpectedAmountForExpense = (
  expense: ExpensePreviewDto,
  sender: TransferParty | null
): number =>
  isSenderParticipant(expense, sender)
    ? (expense.shares ?? [])
        .filter(share => getSharePartyId(share) === sender?.id)
        .reduce((sum, share) => sum + share.amount, 0)
    : expense.amount;

export const getExpectedTotal = (
  expenses: ExpensePreviewDto[],
  sender: TransferParty | null
): number => {
  const total = expenses.reduce(
    (sum, expense) => sum + getExpectedAmountForExpense(expense, sender),
    0
  );
  return Math.round(total * 100) / 100;
};

/**
 * The API refuses a reimbursement mixing an expense the sender takes part in with one they
 * do not: the two cases move the claim differently and could not be undone as a whole.
 */
export const hasMixedReimbursementCases = (
  expenses: ExpensePreviewDto[],
  sender: TransferParty | null
): boolean => new Set(expenses.map(expense => isSenderParticipant(expense, sender))).size > 1;

export const isAmountMatchingSelection = (amount: number, expectedTotal: number): boolean =>
  Math.abs(amount - expectedTotal) <= REIMBURSEMENT_AMOUNT_TOLERANCE;
